import { api } from "../api/apiClient";

const refreshToken = async () => {
  try {
    const response = await api.post(`/api/user/refresh-token`, {
      refreshToken: localStorage.getItem("refresh_token"),
    });
    return response.data;
  } catch (error) {
    console.error("Failed to refresh access token", error);
    return { success: false };
  }
};

export const handleError = async (error, errorMessage) => {
  console.error(errorMessage, error);
  if (error.response && error.response.status === 401) {
    // If the error is due to an expired token, attempt to refresh the token
    const response = await refreshToken();
    if (response.success) {
      // If token refresh is successful, retry the original API call
      const { accessToken } = response;
      if (accessToken) {
        error.config.headers.Authorization = `Bearer ${accessToken}`;
        return api.request(error.config); // Retry the original request
      } else {
        // If there's still no access token, consider it an unauthorized error
        return { unauthorizedError: true };
      }
    } else {
      // If there's still no access token, consider it an unauthorized error
      return { unauthorizedError: true };
    }
  }
  // Re-throw the original error if it's not an unauthorized error
  throw error;
};

export const updateUserSocialMedia = async (userId, socialMediaLinks) => {
  try {
    const response = await api.patch(
      `/api/user/update-user-social-media/${userId}`,
      socialMediaLinks,
    );
    return response.data; // Return the response data
  } catch (error) {
    try {
      return await handleError(
        error,
        "Failed to update the user's social media links.",
      );
    } catch (error) {
      // Handle other errors as needed
      console.error("Error:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    }
  }
};

export const getUserById = async (userId) => {
  try {
    const response = await api.get(`/api/user/get-user-by-id`, {
      params: { userId },
    });
    // Return the decrypted user object from response.data
    return response.data.user;
  } catch (error) {
    try {
      const errorResult = await handleError(
        error,
        "Failed to fetch user data.",
      );
      if (errorResult.unauthorizedError) {
        // Return an error object indicating unauthorized
        return { unauthorizedError: true };
      }
      // Handle other errors as needed
      console.error("Error:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    } catch (error) {
      // Handle errors from the `handleError` function if needed
      console.error("Error in handleError:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    }
  }
};

export const getUserByUsername = async (username) => {
  try {
    const response = await api.get(`/api/user/get-user-by-username`, {
      params: { username },
    });
    // Return the decrypted user object from response.data
    return response.data.user;
  } catch (error) {
    try {
      const errorResult = await handleError(
        error,
        "Failed to fetch user data.",
      );
      if (errorResult.unauthorizedError) {
        return { unauthorizedError: true };
      }
      console.error("Error:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    } catch (error) {
      console.error("Error in handleError:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    }
  }
};

export const getUserByClerkId = async (clerkUserId) => {
  try {
    const response = await api.get(`/api/user/get-user-by-clerk-id`, {
      params: { clerkUserId },
    });
    return response.data;
  } catch (error) {
    try {
      const errorResult = await handleError(
        error,
        "Failed to fetch user data.",
      );
      if (errorResult.unauthorizedError) {
        // Return an error object indicating unauthorized
        return { unauthorizedError: true };
      }
      // Handle other errors as needed
      console.error("Error:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    } catch (error) {
      // Handle errors from the `handleError` function if needed
      console.error("Error in handleError:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    }
  }
};

export const deactivateUser = async (userId) => {
  try {
    const response = await api.post(`/api/user/deactivate-user/${userId}`);
    return response.data;
  } catch (error) {
    try {
      return await handleError(error, "Failed to deactivate user");
    } catch (error) {
      // Handle other errors as needed
      console.error("Error:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    }
  }
};

export const createUser = async (userData) => {
  try {
    const response = await api.post(`/api/user/create-user`, userData);
    return response.data;
  } catch (error) {
    try {
      return await handleError(error, "Failed to create new user");
    } catch (error) {
      // Handle other errors as needed
      console.error("Error:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    }
  }
};

export const updateUser = async (userData) => {
  try {
    const response = await api.patch(`/api/user/update-user`, userData);
    console.log("Updated User: ", response.data);
    return response.data;
  } catch (error) {
    return handleError(error, "Failed to update existing user.");
  }
};

export const updateUserBio = async (userId, bio) => {
  try {
    const response = await api.patch(`/api/user/update-user-bio/${userId}`, {
      bio,
    });
    return response.data;
  } catch (error) {
    try {
      return await handleError(error, "Failed to update the user's bio.");
    } catch (error) {
      // Handle other errors as needed
      console.error("Error:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    }
  }
};

/**
 * Update user lifestyle & interests fields
 */
export const updateUserLifestyle = async (userId, lifestyleData) => {
  try {
    const response = await api.patch(
      `/api/user/update-user-lifestyle/${userId}`,
      lifestyleData,
    );
    return response.data;
  } catch (error) {
    console.error("Error updating lifestyle:", error);
    return {
      success: false,
      message: "Failed to update lifestyle information.",
      error: error.message,
    };
  }
};

export const updateUserAvatar = async (userId, avatar) => {
  try {
    const response = await api.patch(`/api/user/update-user-avatar/${userId}`, {
      avatar,
    });
    return response.data;
  } catch (error) {
    try {
      return await handleError(error, "Failed to update the user's avatar.");
    } catch (error) {
      // Handle other errors as needed
      console.error("Error:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    }
  }
};

export const updateUserName = async (userId, name) => {
  try {
    const response = await api.patch(`/api/user/update-user-name/${userId}`, {
      name,
    });
    return response.data;
  } catch (error) {
    try {
      return await handleError(error, "Failed to update the user's name.");
    } catch (error) {
      console.error("Error:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    }
  }
};

export const updateUserDob = async (userId, dateOfBirth) => {
  try {
    const response = await api.patch(`/api/user/update-user-dob/${userId}`, {
      date_of_birth: dateOfBirth,
    });
    return response.data;
  } catch (error) {
    try {
      return await handleError(error, "Failed to update date of birth.");
    } catch (error) {
      console.error("Error:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    }
  }
};

export const checkDobCooldown = async (userId) => {
  try {
    const response = await api.get(`/api/user/${userId}/dob-cooldown`);
    return response.data;
  } catch (error) {
    console.error("Error checking DOB cooldown:", error);
    return {
      success: false,
      message: "Failed to check cooldown status.",
    };
  }
};

export const updateUserGender = async (userId, gender) => {
  try {
    const response = await api.patch(`/api/user/update-user-gender/${userId}`, {
      gender,
    });
    return response.data;
  } catch (error) {
    try {
      return await handleError(error, "Failed to update gender.");
    } catch (error) {
      console.error("Error:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    }
  }
};

export const setUserStatusActive = async (userId) => {
  try {
    const response = await api.patch(
      `/api/user/set-user-status-active/${userId}`,
    );
    return response.data;
  } catch (error) {
    try {
      return await handleError(
        error,
        "Failed to update the user's status to 'active'.",
      );
    } catch (error) {
      // Handle other errors as needed
      console.error("Error:", error);
      return {
        success: false,
        message: "An error occurred.",
        error: error.message,
      };
    }
  }
};

// User Settings API Functions

/**
 * Fetches available gender options from the server
 * @returns {Promise<Object>} Response containing gender options array
 */
export const getGenderOptions = async () => {
  try {
    const response = await api.get(`/api/user/gender-options`);
    return response.data;
  } catch (error) {
    try {
      const errorResult = await handleError(
        error,
        "Failed to fetch gender options",
      );
      if (errorResult.unauthorizedError) {
        return { unauthorizedError: true };
      }
      console.error("Error:", error);
      return {
        success: false,
        message: "Failed to load gender options",
        error: error.message,
      };
    } catch (error) {
      console.error("Error in handleError:", error);
      return {
        success: false,
        message: "An error occurred",
        error: error.message,
      };
    }
  }
};

/**
 * Retrieves user settings for a specific user
 * @param {string} userId - The ID of the user
 * @returns {Promise<Object>} Response containing user settings
 */
export const getUserSettings = async (userId) => {
  try {
    const response = await api.get(`/api/user/${userId}/settings`);
    return response.data;
  } catch (error) {
    try {
      const errorResult = await handleError(
        error,
        "Failed to fetch user settings",
      );
      if (errorResult.unauthorizedError) {
        return { unauthorizedError: true };
      }
      console.error("Error:", error);
      return {
        success: false,
        message: "Failed to load user settings",
        error: error.message,
      };
    } catch (error) {
      console.error("Error in handleError:", error);
      return {
        success: false,
        message: "An error occurred",
        error: error.message,
      };
    }
  }
};

/**
 * Saves user settings to the server
 * @param {string} userId - The ID of the user
 * @param {Object} settings - The settings object to save
 * @returns {Promise<Object>} Response indicating success or failure
 */
export const saveUserSettings = async (userId, settings) => {
  console.log("[API] Saving settings for user:", userId, settings);
  try {
    const response = await api.put(`/api/user/${userId}/settings`, settings);

    if (!response.data.success) {
      throw new Error(response.data.error || "Settings update failed");
    }

    return {
      success: true,
      settings: response.data.settings,
    };
  } catch (error) {
    console.error("[API] Error:", error);
    // Convert api error to a more useful format
    if (error.response) {
      throw new Error(error.response.data.error || "Server error");
    } else {
      throw error;
    }
  }
};

// usersAPI.js

/**
 * Retrieves the user's monthly usage data (e.g., creations, joins).
 * Throws an Error if the fetch fails or is unauthorized.
 * @param {string} userId - The ID of the user (Clerk User ID).
 * @returns {Promise<{success: true, creations: number, joins: number}>} Response containing usage data.
 */
export const getUserMonthlyUsage = async (userId) => {
  try {
    const response = await api.get(`/api/user/${userId}/usage/monthly`);

    // Check for explicit non-success from the backend response data structure
    if (!response.data.success) {
      throw new Error(response.data.error || "Usage fetch failed");
    }

    // Return the successful data structure
    return {
      success: true,
      // response.data contains { success: true, creations: N, joins: M, ... }
      ...response.data,
    };
  } catch (error) {
    // Pass ALL errors to the centralized handler.
    // handleError is responsible for retrying (if 401) or re-throwing the error.
    await handleError(error, "Failed to fetch user monthly usage");

    throw error;
  }
};

/**
 * Fetches all organizations (Schools/Colleges) for selection
 */
export const getAllOrganizations = async () => {
  try {
    const response = await api.get(`/api/user/organizations`);
    return response.data;
  } catch (error) {
    return await handleError(error, "Failed to fetch organizations");
  }
};

/**
 * Updates the user's campus/study status
 * @param {string} userId - UUID of the user
 * @param {string} status - 'Free', 'Studying', or 'Busy'
 */
export const updatePublicStatus = async (userId, status) => {
  try {
    const response = await api.patch(`/api/user/update-public-status`, {
      userId,
      public_status: status,
    });
    return response; // Returning full Axios response as requested
  } catch (error) {
    return await handleError(error, "Failed to update public status.");
  }
};

export const getFriendsList = async (userId) => {
  try {
    const response = await api.get(`/api/user/${userId}/friends`);
    return response.data;
  } catch (error) {
    console.error("Fetch friends error", error);
    return { success: false, friends: [] };
  }
};

/**
 * Searches for users by name or username
 * @param {string} query - The search string
 * @returns {Promise<Object>} Response containing userList array
 */
export const searchUsers = async (query) => {
  try {
    const response = await api.get(`/api/user/search`, {
      params: { q: query },
    });
    return response.data; // Should return { success: true, userList: [...] }
  } catch (error) {
    try {
      return await handleError(error, "Failed to search users.");
    } catch (err) {
      console.error("Error:", err);
      return { success: false, userList: [] };
    }
  }
};

/**
 * Gets people you may know based on mutual friends
 * @param {number} limit - Maximum number of suggestions to return
 * @returns {Promise<Object>} Response containing suggestions array
 */
export const getPeopleYouMayKnow = async (limit = 10) => {
  try {
    const response = await api.get(`/api/user/people-you-may-know`, {
      params: { limit },
    });
    return response.data; // { success: true, suggestions: [...] }
  } catch (error) {
    try {
      return await handleError(error, "Failed to get suggestions.");
    } catch (err) {
      console.error("Error:", err);
      return { success: false, suggestions: [] };
    }
  }
};

/**
 * Sends a friend request to another user.
 * @param {string} requesterId - Current user ID (Local UUID)
 * @param {string} targetUserId - Target user ID (Local UUID)
 */
export const sendFriendRequest = async (requesterId, targetUserId) => {
  try {
    const response = await api.post(`/api/user/friends/request`, {
      requesterId,
      targetUserId,
    });
    return response.data; // { success: true, message: 'Friend request sent!', friendship: { ... } }
  } catch (error) {
    return await handleError(error, "Failed to send friend request.");
  }
};

/**
 * @param {string} requesterId - Your ID
 * @param {string} targetUserId - The other user's ID
 * @param {string} status - 'accepted', 'declined', or 'blocked'
 */
export const handleFriendRequest = async (
  requesterId,
  targetUserId,
  status,
) => {
  try {
    const response = await api.patch(`/api/user/friends/handle`, {
      requesterId,
      targetUserId,
      status,
    });
    return response.data;
  } catch (error) {
    return await handleError(error, "Failed to handle friend request.");
  }
};

export const getPendingRequests = async (userId) => {
  try {
    const response = await api.get(`/api/user/${userId}/friends/requests`);
    return response.data;
  } catch (error) {
    return await handleError(error, "Failed to fetch pending requests.");
  }
};

export const getNotificationPreferences = async (userId) => {
  try {
    const response = await api.get(
      `/api/user/${userId}/notification-preferences`,
    );
    return response.data;
  } catch (error) {
    return await handleError(
      error,
      "Failed to fetch notification preferences.",
    );
  }
};

export const updateNotificationPreferences = async (
  userId,
  silencedNotificationTypes,
) => {
  try {
    const response = await api.put(
      `/api/user/${userId}/notification-preferences`,
      {
        silencedNotificationTypes,
      },
    );
    return response.data;
  } catch (error) {
    return await handleError(
      error,
      "Failed to update notification preferences.",
    );
  }
};

/**
 * Mark user's onboarding as complete
 * Called when user reaches /home for the first time
 */
export const markOnboardingComplete = async (userId) => {
  try {
    console.log(`[API] Marking onboarding complete for user: ${userId}`);
    const response = await api.post(`/api/user/${userId}/onboarding-complete`);
    console.log("[API] Onboarding complete response:", response.data);
    return response.data;
  } catch (error) {
    // Don't throw - this is a background operation
    console.error("[API] Failed to mark onboarding complete:", error);
    return { success: false };
  }
};
